import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function parseValue(value: string): { date: Date | null; hour: number; minute: number } {
  if (!value) return { date: null, hour: 19, minute: 0 };
  const [d, t] = value.split("T");
  const [y, m, day] = d.split("-").map(Number);
  const [h, min] = (t ?? "19:00").split(":").map(Number);
  if (!y || !m || !day) return { date: null, hour: 19, minute: 0 };
  return {
    date: new Date(y, m - 1, day),
    hour: Number.isFinite(h) ? h : 19,
    minute: Number.isFinite(min) ? min : 0,
  };
}

function formatValue(date: Date, hour: number, minute: number): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(hour)}:${pad(minute)}`;
}

function formatDisplay(date: Date, hour: number, minute: number): string {
  return `${MONTHS[date.getMonth()].slice(0, 3)} ${date.getDate()}, ${date.getFullYear()} · ${pad(hour)}:${pad(minute)}`;
}

function buildMonthGrid(viewYear: number, viewMonth: number): Date[] {
  const first = new Date(viewYear, viewMonth, 1);
  const dayOfWeek = (first.getDay() + 6) % 7;
  const start = new Date(viewYear, viewMonth, 1 - dayOfWeek);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick date & time",
  className,
}: DateTimePickerProps) {
  const parsed = parseValue(value);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(
    parsed.date ? parsed.date.getFullYear() : new Date().getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(
    parsed.date ? parsed.date.getMonth() : new Date().getMonth(),
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (parsed.date) {
      setViewYear(parsed.date.getFullYear());
      setViewMonth(parsed.date.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const today = new Date();

  function changeMonth(delta: number) {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  }

  function selectDay(d: Date) {
    onChange(formatValue(d, parsed.hour, parsed.minute));
  }

  function changeTime(hour: number, minute: number) {
    const base = parsed.date ?? today;
    onChange(formatValue(base, hour, minute));
  }

  function clear() {
    onChange("");
  }

  function setToday() {
    const now = new Date();
    onChange(formatValue(now, now.getHours(), 0));
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  }

  const display = parsed.date ? formatDisplay(parsed.date, parsed.hour, parsed.minute) : "";

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center justify-between rounded-[10px] border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 text-left text-[15px] text-[#1a1a1a] transition-colors hover:border-[#cbd5e1] focus:border-[#2563eb] focus:bg-white focus:outline-none",
          !display && "text-[#94a3b8]",
        )}
      >
        <span className="truncate">{display || placeholder}</span>
        <Calendar className="h-4 w-4 shrink-0 text-[#64748b]" />
      </button>

      {open ? (
        <div className="absolute left-0 top-[calc(100%+6px)] z-40 w-[320px] rounded-[14px] border border-[#e2e8f0] bg-white p-4 shadow-[0_16px_48px_rgba(15,23,42,0.12)]">
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-bold text-[#1a1a1a]">
              {MONTHS[viewMonth]} {viewYear}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                aria-label="Previous month"
                className="grid h-7 w-7 place-items-center rounded-[8px] text-[#475569] hover:bg-[#f1f5f9]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                aria-label="Next month"
                className="grid h-7 w-7 place-items-center rounded-[8px] text-[#475569] hover:bg-[#f1f5f9]"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-7 gap-y-1 text-center text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
            {WEEKDAYS.map((w) => (
              <div key={w}>{w[0]}</div>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-7 gap-1">
            {grid.map((d) => {
              const inMonth = d.getMonth() === viewMonth;
              const isSelected = parsed.date ? isSameDay(d, parsed.date) : false;
              const isToday = isSameDay(d, today);
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  onClick={() => selectDay(d)}
                  className={cn(
                    "h-8 rounded-[8px] text-[13px] transition-colors",
                    inMonth ? "text-[#1a1a1a]" : "text-[#cbd5e1]",
                    !isSelected && "hover:bg-[#f1f5f9]",
                    isToday && !isSelected && "font-bold text-[#2563eb]",
                    isSelected && "bg-[#2563eb] font-bold text-white hover:bg-[#1d4ed8]",
                  )}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center gap-2 border-t border-[#f1f5f9] pt-3">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-[#64748b]">
              Time
            </span>
            <input
              type="number"
              min={0}
              max={23}
              value={pad(parsed.hour)}
              onChange={(e) => changeTime(Math.min(23, Math.max(0, Number(e.target.value) || 0)), parsed.minute)}
              className="w-14 rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] px-2 py-1.5 text-center text-[14px] tabular-nums focus:border-[#2563eb] focus:bg-white focus:outline-none"
            />
            <span className="text-[14px] text-[#94a3b8]">:</span>
            <input
              type="number"
              min={0}
              max={59}
              step={5}
              value={pad(parsed.minute)}
              onChange={(e) => changeTime(parsed.hour, Math.min(59, Math.max(0, Number(e.target.value) || 0)))}
              className="w-14 rounded-[8px] border border-[#e2e8f0] bg-[#f8fafc] px-2 py-1.5 text-center text-[14px] tabular-nums focus:border-[#2563eb] focus:bg-white focus:outline-none"
            />
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={clear}
              className="text-[13px] font-semibold text-[#94a3b8] hover:text-[#64748b]"
            >
              Clear
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={setToday}
                className="rounded-[8px] px-3 py-1.5 text-[13px] font-semibold text-[#2563eb] hover:bg-[#eff6ff]"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-[8px] bg-[#2563eb] px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-[#1d4ed8]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
